import React, { useEffect, useRef, useState } from "react";
import { getAllStaticPagesData } from "../../services/StaticPages";
import TableStructure from "../../CommonComponents/TableStructure";
import { Box, Text, useToast } from "@chakra-ui/react";
import { handleExport as handleExportUtil } from '../../util/helper';

const PublicTable = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("slug");
  const limit = 50;
  const toast = useToast();
  
  // Fetch static pages from the API
  const fetchStaticPages = async (page, term) => {
    setLoading(true);
    try {
      const result = await getAllStaticPagesData(limit, page, term);
      if (result?.data) {
        const processedData = processStaticPages(result?.data?.data || []);
        const sortedData = sortData(processedData, sortField, sortOrder);
        setRecords(sortedData);
        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);

        if (result.data.page_number && parseInt(result.data.page_number) !== currentPage) {
          setCurrentPage(parseInt(result.data.page_number));
        }
      }
    } catch (error) {
      console.error("Error fetching static pages:", error);
    } finally {
      setLoading(false);
    }
  };

  const processStaticPages = (data) => {
    return data?.map((item) => {
      return {
        slug: item?.slug,
        title: item?.title,
        language: item?.language?.toUpperCase() || 'EN',
        createdBy: item?.createdBy || 'N/A',
        status: item?.status || 'draft',
        path: item?.path,
        createdAt: item?.createdAt || ''
      }
    });
  }

  // Sort data helper function
  const sortData = (data, field, order) => {
    return [...data].sort((a, b) => {
      if (field === 'createdAt') {
        const dateA = a[field] ? new Date(a[field]).getTime() : 0;
        const dateB = b[field] ? new Date(b[field]).getTime() : 0;
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      } else {
        return order === 'asc' 
          ? String(a[field] || '').localeCompare(String(b[field] || ''))
          : String(b[field] || '').localeCompare(String(a[field] || ''));
      }
    });
  };

  // Handle column header click for sorting
  const handleSort = (field) => {
    const newOrder = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    
    const sorted = sortData(records, field, newOrder);
    setRecords(sorted);
  };

  const columns = [
    {
      label: "Slug",
      accessor: "slug",
      sortable: true,
      onSort: () => handleSort('slug')
    },
    {
      label: "Title",
      accessor: "title",
      sortable: true,
      onSort: () => handleSort('title')
    },
    {
      label: "Language",
      accessor: "language",
      sortable: true,
      onSort: () => handleSort('language'),
      render: (value) => (
        <Box>
          <Text fontWeight="500">{value}</Text>
        </Box>
      )
    },
    {
      label: "Created By",
      accessor: "createdBy",
      render: (value) => (
        <Box>
          <Text noOfLines={1}>{value}</Text>
        </Box>
      )
    },
    {
      label: "Status",
      accessor: "status",
      render: (value) => (
        <Box>
          <Text 
            px={2} 
            py={1} 
            borderRadius="md" 
            bg={value === 'published' ? 'green.100' : 'yellow.100'}
            color={value === 'published' ? 'green.800' : 'yellow.800'}
            fontSize="sm"
            fontWeight="500"
            display="inline-block"
          >
            {value}
          </Text>
        </Box>
      )
    }
  ];

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false;
      setCurrentPage(1);
      setSearchText(searchTerm);
      fetchStaticPages(1, searchTerm);
    }
  };

  // Legacy sort function (kept for compatibility with TableStructure)
  const sortRecords = () => {
    handleSort(sortField);
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchStaticPages(currentPage, searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleClear = () => {
    setSearchText('');
    fetchStaticPages(1, "");
  };

  const handleExport = () => handleExportUtil("public", toast);

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  return (
      <TableStructure
        isAuthenticated={true}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
        sortRecords={sortRecords}
        sortOrder={sortOrder}
        loading={loading}
        records={records}
        totalRecords={totalRecords}
        type="public"
        title="Public Pages"
        addOption="true"
        columns={columns}
        sortField={sortField}
        exportHandler={handleExport}
        identifierField="slug"
    />
  );
};

export default PublicTable;
